import type { CallParticipant } from '@shareview/tele-engine-core';
import { useAttachedTrack } from '@shareview/tele-engine-react';

interface TileProps {
  participant: CallParticipant;
}

/**
 * One participant: camera video plus name and state label. Remote audio is
 * rendered here too. Tracks render through `session.attach`, so adaptive
 * stream sizes each remote layer from this visible element.
 */
export function ParticipantTile({ participant }: TileProps) {
  const { isLocal } = participant;
  const video = participant.tracks.find((t) => t.kind === 'video');
  const audio = participant.tracks.find((t) => t.kind === 'audio');
  const videoRef = useAttachedTrack(video);
  const audioRef = useAttachedTrack(isLocal ? null : audio);

  const camOff = !video || video.muted || !video.mediaStreamTrack;
  const micOff = !audio || audio.muted;

  const flags = [
    isLocal ? '(you)' : null,
    micOff ? '[mic off]' : null,
    camOff ? '[cam off]' : null,
    video?.codec ? video.codec.toUpperCase() : null,
    !isLocal && participant.connectionQuality !== 'unknown' ? `q=${participant.connectionQuality}` : null,
    participant.connectionState !== 'connected' && !isLocal ? participant.connectionState : null,
    participant.mediaStalled ? 'STALLED' : null,
  ].filter(Boolean);

  return (
    <div
      className="tile"
      data-testid="tile"
      data-participant={participant.id}
      data-local={isLocal ? 'true' : 'false'}
      data-cam-muted={camOff ? 'true' : 'false'}
      data-mic-muted={micOff ? 'true' : 'false'}
    >
      {video && <video ref={videoRef} autoPlay playsInline muted className={isLocal ? 'mirror' : ''} />}
      {camOff && <div className="tile-placeholder">camera off</div>}
      {!isLocal && audio && <audio ref={audioRef} autoPlay />}
      <div className="tile-label">
        {participant.displayName || participant.id} {flags.join(' ')}
      </div>
    </div>
  );
}
